import axios from 'axios';
import type { CryptoCurrency } from '~/generated/prisma';

export interface CreatePaymentData {
  currency: CryptoCurrency;
  network?: string;
  amountUsd: number;
  /**
   * Credits to purchase (if this is a credit purchase)
   * If 0, this is a wallet funding payment
   * If > 0, this is a credit purchase payment
   * If undefined, will be calculated based on amount
   */
  creditsPurchased?: number;
}

export interface PaymentResponse {
  id: string;
  userId: string;
  currency: CryptoCurrency;
  network?: string | null;
  amountCrypto: number;
  amountUsd: number;
  creditsPurchased: number;
  walletAddress: string;
  transactionHash?: string | null;
  status: string;
  confirmations: number;
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
  confirmedAt?: string | null;
  cancelledAt?: string | null;
}

/**
 * Get active payment for current user
 *
 * @param purpose - Payment purpose filter ('wallet' for wallet funding, 'credits' for credit purchase)
 */
export const getActivePaymentApi = async (
  purpose?: 'wallet' | 'credits',
): Promise<PaymentResponse | null> => {
  const params = new URLSearchParams({ type: 'active' });
  if (purpose) {
    params.append('purpose', purpose);
  }

  const response = await axios.get<{
    success: boolean;
    data: PaymentResponse | null;
  }>(`/api/crypto/payments?${params.toString()}`);
  return response.data.data;
};

/**
 * Create a new payment
 */
export const createPaymentApi = async (
  data: CreatePaymentData,
): Promise<PaymentResponse> => {
  const response = await axios.post<{
    success: boolean;
    message: string;
    data: PaymentResponse;
  }>('/api/crypto/payments', data);
  return response.data.data;
};

/**
 * Cancel a payment
 */
export const cancelPaymentApi = async (
  paymentId: string,
): Promise<PaymentResponse> => {
  const response = await axios.post<{
    success: boolean;
    message: string;
    data: PaymentResponse;
  }>(`/api/crypto/payments/${paymentId}/cancel`);
  return response.data.data;
};

/**
 * Verify a payment
 */
export const verifyPaymentApi = async (
  paymentId: string,
): Promise<{
  success: boolean;
  message: string;
  data: PaymentResponse & { confirmations?: number; required?: number };
}> => {
  const response = await axios.post<{
    success: boolean;
    message: string;
    data: PaymentResponse & { confirmations?: number; required?: number };
  }>(`/api/crypto/payments/${paymentId}/verify`);
  return response.data;
};
